import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchSecurity, changePassword, signOutAllSessions, setup2fa, enable2fa, disable2fa, getRole, mfaEnrollmentRequired, managerHomePath } from "../services/api";
import { useT } from "../i18n/useT";

// Account security (SEC-1): change password, turn on/off two-factor
// authentication (TOTP), and sign out of all other devices.
const S = {
  en: {
    title: "Security", subtitle: "Protect your account — password, two-factor authentication and devices.",
    cp: "Change password", current: "Current password", newp: "New password", confirm: "Confirm new password",
    cp_btn: "Update password", cp_ok: "Password updated. Other devices have been signed out.",
    mismatch: "New passwords do not match.", weak: "Password must be at least 8 characters and include a letter and a number.",
    twofa: "Two-factor authentication", twofa_on: "Two-factor authentication is ON.", twofa_off: "Two-factor authentication is off.",
    twofa_desc: "Add a second step at sign-in using an authenticator app (Google Authenticator, Authy…).",
    enable: "Enable 2FA", disable: "Disable 2FA", scan: "Scan this QR code in your authenticator app, then enter the 6-digit code to confirm.",
    or_key: "Or enter this key manually:", code: "6-digit code", confirm_enable: "Confirm & enable",
    disable_prompt: "Enter a current code to turn 2FA off:", cancel: "Cancel",
    devices: "Devices", devices_desc: "Signed in on a device you no longer use? Sign out everywhere except here.",
    signout_all: "Sign out all other devices", signout_ok: "All other sessions have been signed out.",
    err: "Something went wrong. Try again.",
    mfa_req_title: "Two-factor authentication is required for your account",
    mfa_req_desc: "Your role requires 2FA. Enable it below to finish signing in — the rest of the dashboard is locked until you do.",
  },
  am: {
    title: "ደህንነት", subtitle: "መለያዎን ይጠብቁ — የይለፍ ቃል፣ ባለ ሁለት ደረጃ ማረጋገጫ እና መሣሪያዎች።",
    cp: "የይለፍ ቃል ቀይር", current: "የአሁኑ የይለፍ ቃል", newp: "አዲስ የይለፍ ቃል", confirm: "አዲሱን አረጋግጥ",
    cp_btn: "የይለፍ ቃል አዘምን", cp_ok: "የይለፍ ቃል ተቀይሯል። ሌሎች መሣሪያዎች ወጥተዋል።",
    mismatch: "አዲሶቹ የይለፍ ቃላት አይዛመዱም።", weak: "የይለፍ ቃል ቢያንስ 8 ቁምፊ፣ ፊደልና ቁጥር ሊኖረው ይገባል።",
    twofa: "ባለ ሁለት ደረጃ ማረጋገጫ", twofa_on: "ባለ ሁለት ደረጃ ማረጋገጫ በርቷል።", twofa_off: "ባለ ሁለት ደረጃ ማረጋገጫ ጠፍቷል።",
    twofa_desc: "በመግቢያ ጊዜ በauthenticator መተግበሪያ ሁለተኛ ደረጃ ያክሉ።",
    enable: "2FA አብራ", disable: "2FA አጥፋ", scan: "ይህን QR በauthenticator መተግበሪያ ይቃኙ፣ ከዚያ 6-አሃዝ ኮድ ያስገቡ።",
    or_key: "ወይም ይህን ቁልፍ በእጅ ያስገቡ፡", code: "6-አሃዝ ኮድ", confirm_enable: "አረጋግጥና አብራ",
    disable_prompt: "2FA ለማጥፋት የአሁኑን ኮድ ያስገቡ፡", cancel: "ሰርዝ",
    devices: "መሣሪያዎች", devices_desc: "ከዚህ ውጭ ካሉ መሣሪያዎች ይውጡ።",
    signout_all: "ሁሉንም ሌሎች መሣሪያዎች አስውጣ", signout_ok: "ሁሉም ሌሎች ክፍለ-ጊዜዎች ወጥተዋል።",
    err: "ስህተት ተፈጥሯል። እንደገና ይሞክሩ።",
    mfa_req_title: "ለመለያዎ ባለ ሁለት ደረጃ ማረጋገጫ ያስፈልጋል",
    mfa_req_desc: "የእርስዎ ሚና 2FA ይጠይቃል። መግባትን ለማጠናቀቅ ከታች ያብሩት — እስከዚያ ድረስ ቀሪው ዳሽቦርድ ተቆልፏል።",
  },
  om: {
    title: "Nageenya", subtitle: "Herrega kee eegi — jecha darbii, mirkaneessa sadarkaa lamaa fi meeshaalee.",
    cp: "Jecha darbii jijjiiri", current: "Jecha darbii ammaa", newp: "Jecha darbii haaraa", confirm: "Haaraa mirkaneessi",
    cp_btn: "Jecha darbii haaromsi", cp_ok: "Jechi darbii haaromfame. Meeshaaleen biroo ba'aniiru.",
    mismatch: "Jechoonni darbii haaraa wal hin simne.", weak: "Jechi darbii yoo xiqqaate qubee 8, qubee fi lakkoofsa qabaachuu qaba.",
    twofa: "Mirkaneessa sadarkaa lamaa", twofa_on: "Mirkaneessi sadarkaa lamaa ON dha.", twofa_off: "Mirkaneessi sadarkaa lamaa cufaa dha.",
    twofa_desc: "Yeroo seenaa app authenticator fayyadamuun tarkaanfii lammaffaa dabali.",
    enable: "2FA banii", disable: "2FA cufi", scan: "QR kana app authenticator keessatti scan godhi, koodii lakkoofsa 6 galchi.",
    or_key: "Yookiin furtuu kana harkaan galchi:", code: "Koodii lakkoofsa 6", confirm_enable: "Mirkaneessii banii",
    disable_prompt: "2FA cufuuf koodii ammaa galchi:", cancel: "Haqi",
    devices: "Meeshaalee", devices_desc: "Meeshaalee asii ala jiran hunda irraa ba'i.",
    signout_all: "Meeshaalee biroo hunda irraa ba'i", signout_ok: "Sessiiwwan biroo hundi ba'aniiru.",
    err: "Wanti dogoggore. Irra deebi'i yaali.",
    mfa_req_title: "Herrega keessaniif mirkaneessi sadarkaa lamaa barbaachisaa dha",
    mfa_req_desc: "Gaheen kee 2FA barbaada. Seenuu xumuruuf gadii banii — hanga sanitti daashboordiin hafe cufaadha.",
  },
};

const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)", padding: 18, marginTop: 14 };
const inp = { padding: 10, borderRadius: 9, border: "1px solid #d8dee2", fontSize: 14, width: "100%", boxSizing: "border-box" };
const lbl = { fontSize: 12.5, fontWeight: 600, color: "#334155", display: "block", margin: "10px 0 4px" };

export default function SecurityPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const navigate = useNavigate();
  const role = getRole() || "manager";
  const isManager = role === "manager";
  const mustEnroll = mfaEnrollmentRequired();   // forced 2FA enrollment gate

  const [twoFa, setTwoFa] = useState(null);
  const [cp, setCp] = useState({ current: "", newp: "", confirm: "" });
  const [msg, setMsg] = useState({ cp: "", tf: "", dev: "" });
  const [busy, setBusy] = useState("");
  const [enroll, setEnroll] = useState(null);   // {qr, secret} while enabling
  const [enrollCode, setEnrollCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [disabling, setDisabling] = useState(false);

  useEffect(() => { fetchSecurity().then((d) => setTwoFa(d.two_factor_enabled)).catch(() => setTwoFa(false)); }, []);

  const doChangePw = async () => {
    setMsg((m) => ({ ...m, cp: "" }));
    if (cp.newp !== cp.confirm) { setMsg((m) => ({ ...m, cp: t("mismatch") })); return; }
    if (cp.newp.length < 8 || !/[a-zA-Z]/.test(cp.newp) || !/\d/.test(cp.newp)) { setMsg((m) => ({ ...m, cp: t("weak") })); return; }
    setBusy("cp");
    try { await changePassword(cp.current, cp.newp); setCp({ current: "", newp: "", confirm: "" }); setMsg((m) => ({ ...m, cp: "✓ " + t("cp_ok") })); }
    catch (e) { setMsg((m) => ({ ...m, cp: e?.response?.data?.detail || t("err") })); }
    finally { setBusy(""); }
  };
  const startEnroll = async () => { setMsg((m) => ({ ...m, tf: "" })); setBusy("tf"); try { setEnroll(await setup2fa()); } catch { setMsg((m) => ({ ...m, tf: t("err") })); } finally { setBusy(""); } };
  const confirmEnroll = async () => {
    setBusy("tf");
    try {
      await enable2fa(enrollCode); setTwoFa(true); setEnroll(null); setEnrollCode("");
      // If this was a forced enrollment, the session is now upgraded (fresh
      // token, gate cleared) — send the user into the dashboard.
      if (mustEnroll) navigate(managerHomePath(), { replace: true });
    }
    catch (e) { setMsg((m) => ({ ...m, tf: e?.response?.data?.detail || t("err") })); }
    finally { setBusy(""); }
  };
  const doDisable = async () => {
    setBusy("tf");
    try { await disable2fa(disableCode); setTwoFa(false); setDisabling(false); setDisableCode(""); }
    catch (e) { setMsg((m) => ({ ...m, tf: e?.response?.data?.detail || t("err") })); }
    finally { setBusy(""); }
  };
  const doSignOutAll = async () => {
    setBusy("dev"); setMsg((m) => ({ ...m, dev: "" }));
    try { await signOutAllSessions(); setMsg((m) => ({ ...m, dev: "✓ " + t("signout_ok") })); }
    catch { setMsg((m) => ({ ...m, dev: t("err") })); } finally { setBusy(""); }
  };

  return (
    <div style={{ maxWidth: 640, margin: "0 auto" }}>
      <h1 style={{ marginBottom: 2 }}>{t("title")}</h1>
      <div className="muted" style={{ fontSize: 14 }}>{t("subtitle")}</div>

      {/* Mandatory-enrollment banner: the account is in a restricted session
          until 2FA is enabled (server policy MFA_REQUIRED_ROLES). */}
      {mustEnroll && !twoFa && (
        <div style={{ background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 12, padding: 14, marginTop: 14 }}>
          <div style={{ fontWeight: 700, color: "#92400E" }}>🔒 {t("mfa_req_title")}</div>
          <div style={{ fontSize: 13.5, color: "#92400E", marginTop: 4 }}>{t("mfa_req_desc")}</div>
        </div>
      )}

      {/* change password */}
      <div style={CARD}>
        <div style={{ fontWeight: 700, color: "#063b48" }}>{t("cp")}</div>
        <label style={lbl}>{t("current")}</label>
        <input style={inp} type="password" value={cp.current} onChange={(e) => setCp({ ...cp, current: e.target.value })} />
        <label style={lbl}>{t("newp")}</label>
        <input style={inp} type="password" value={cp.newp} onChange={(e) => setCp({ ...cp, newp: e.target.value })} />
        <label style={lbl}>{t("confirm")}</label>
        <input style={inp} type="password" value={cp.confirm} onChange={(e) => setCp({ ...cp, confirm: e.target.value })} />
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12 }}>
          <button className="btn primary" disabled={busy === "cp"} onClick={doChangePw}>{t("cp_btn")}</button>
          {msg.cp && <span style={{ fontSize: 13, color: msg.cp.startsWith("✓") ? "#1D7A44" : "#A32D2D" }}>{msg.cp}</span>}
        </div>
      </div>

      {/* 2FA */}
      <div style={CARD}>
        <div style={{ fontWeight: 700, color: "#063b48" }}>{t("twofa")}</div>
        <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>{t("twofa_desc")}</div>
        {twoFa == null ? <p className="muted">…</p> : (
          <>
            <div style={{ margin: "10px 0", fontSize: 13.5, fontWeight: 600, color: twoFa ? "#1D7A44" : "#8a949b" }}>
              {twoFa ? t("twofa_on") : t("twofa_off")}
            </div>
            {!twoFa && !enroll && <button className="btn primary small" disabled={busy === "tf"} onClick={startEnroll}>{t("enable")}</button>}
            {!twoFa && enroll && (
              <div>
                <div style={{ fontSize: 13, marginBottom: 8 }}>{t("scan")}</div>
                <img src={enroll.qr} alt="2FA QR" style={{ width: 180, height: 180, border: "1px solid #eef2f2", borderRadius: 8 }} />
                <div className="muted" style={{ fontSize: 12, margin: "6px 0" }}>{t("or_key")} <code style={{ fontSize: 12 }}>{enroll.secret}</code></div>
                <input style={{ ...inp, maxWidth: 200 }} inputMode="numeric" placeholder={t("code")} value={enrollCode} onChange={(e) => setEnrollCode(e.target.value.replace(/\D/g, "").slice(0, 6))} />
                <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                  <button className="btn primary small" disabled={busy === "tf"} onClick={confirmEnroll}>{t("confirm_enable")}</button>
                  <button className="btn secondary small" onClick={() => { setEnroll(null); setEnrollCode(""); }}>{t("cancel")}</button>
                </div>
              </div>
            )}
            {twoFa && !disabling && <button className="btn secondary small" onClick={() => setDisabling(true)}>{t("disable")}</button>}
            {twoFa && disabling && (
              <div>
                <div style={{ fontSize: 13, marginBottom: 6 }}>{t("disable_prompt")}</div>
                <input style={{ ...inp, maxWidth: 200 }} inputMode="numeric" placeholder={t("code")} value={disableCode} onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))} />
                <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                  <button className="btn secondary small" disabled={busy === "tf"} onClick={doDisable}>{t("disable")}</button>
                  <button className="btn secondary small" onClick={() => { setDisabling(false); setDisableCode(""); }}>{t("cancel")}</button>
                </div>
              </div>
            )}
            {msg.tf && <div style={{ fontSize: 13, color: "#A32D2D", marginTop: 8 }}>{msg.tf}</div>}
          </>
        )}
      </div>

      {/* devices / sign out all */}
      {isManager && (
        <div style={CARD}>
          <div style={{ fontWeight: 700, color: "#063b48" }}>{t("devices")}</div>
          <div className="muted" style={{ fontSize: 13, marginTop: 4, marginBottom: 10 }}>{t("devices_desc")}</div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button className="btn secondary" disabled={busy === "dev"} onClick={doSignOutAll}>{t("signout_all")}</button>
            {msg.dev && <span style={{ fontSize: 13, color: msg.dev.startsWith("✓") ? "#1D7A44" : "#A32D2D" }}>{msg.dev}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
